import jsPDF from "jspdf";
import "jspdf-autotable";

/**
 * Common PDF export configuration
 */
const PDF_CONFIG = {
  orientation: "landscape",
  unit: "mm",
  format: "a4",
  margins: { top: 15, right: 10, bottom: 15, left: 10 },
};

/**
 * Add header to PDF
 */
const addPDFHeader = (doc, title, subtitle, financialYear) => {
  // Add title
  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.text(title, 15, 15);

  // Add subtitle
  if (subtitle) {
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    doc.text(subtitle, 15, 22);
  }

  // Add financial year and date
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFontSize(9);
  doc.setFont(undefined, "normal");

  if (financialYear) {
    doc.text(`Financial Year: ${financialYear}`, pageWidth - 15, 15, {
      align: "right",
    });
  }

  const exportDate = new Date().toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  doc.text(`Exported: ${exportDate}`, pageWidth - 15, 20, { align: "right" });

  return 28; // Return Y position for content start
};

/**
 * Format currency for PDF
 */
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "0.00";
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) return "0.00";
  return `${numAmount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Format status for PDF
 */
const getStatusLabel = (status) => {
  const statusMap = {
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
  };
  return statusMap[status] || status;
};

/**
 * Format date for PDF
 */
const formatDate = (date) => {
  if (!date) return "—";
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Export Project History to PDF (Department User View)
 * Updated with portrait layout and official government format
 */
export const exportProjectHistoryPDF = (projects, departmentName, filters) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;

  // Header - Government of Nagaland
  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.text("Government of Nagaland", pageWidth / 2, 20, { align: "center" });

  // Department name
  doc.setFontSize(14);
  doc.setFont(undefined, "bold");
  doc.text(`Department of - ${departmentName}`, pageWidth / 2, 28, {
    align: "center",
  });

  // Title
  doc.setFontSize(12);
  doc.setFont(undefined, "bold");
  doc.text("New Project Proposal Status", pageWidth / 2, 38, {
    align: "center",
  });

  // Financial Year and Export Date
  doc.setFontSize(9);
  doc.setFont(undefined, "normal");
  doc.text(
    `Financial Year: ${filters.financialYear || "All Years"}`,
    margin,
    46,
  );

  const exportDate = new Date().toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  doc.text(`Exported: ${exportDate}`, pageWidth - margin, 46, {
    align: "right",
  });

  // Prepare table data - only columns visible to department users
  const tableData = [];

  projects.forEach((project, index) => {
    tableData.push([
      index + 1,
      project.projectName,
      project.financialYear ? `FY ${project.financialYear}` : "N/A",
      formatCurrency(project.financialRequirement),
      project.priority || "—",
      formatDate(project.createdAt),
    ]);
  });

  // Calculate total
  const totalProposed = projects.reduce(
    (sum, p) => sum + (parseFloat(p.financialRequirement) || 0),
    0,
  );

  // Create table
  const startY = 52;

  doc.autoTable({
    startY: startY,
    head: [
      [
        "S.No.",
        "Project Name",
        "Financial\nYear",
        "Proposed Total Cost\n(in lakh)",
        "Priority",
        "Submitted",
      ],
    ],
    body: tableData,
    foot: [["", "Total", "", formatCurrency(totalProposed), "", ""]],
    theme: "grid",
    headStyles: {
      fillColor: [71, 85, 105],
      textColor: 255,
      fontSize: 9,
      fontStyle: "bold",
      halign: "center",
      valign: "middle",
    },
    bodyStyles: {
      fontSize: 9,
      valign: "middle",
    },
    footStyles: {
      fillColor: [31, 41, 55],
      textColor: 255,
      fontSize: 9,
      fontStyle: "bold",
      halign: "right",
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 15 },
      1: { halign: "left", cellWidth: 80 },
      2: { halign: "center", cellWidth: 22 },
      3: { halign: "right", cellWidth: 30 },
      4: { halign: "center", cellWidth: 18 },
      5: { halign: "center", cellWidth: 25 },
    },
    margin: { top: startY, left: margin, right: margin },
    didDrawPage: function (data) {
      // Page number at bottom center
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.text(
        `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" },
      );
    },
  });

  // Add signature block at bottom right with more space
  const finalY = doc.lastAutoTable.finalY + 40;
  const signatureX = pageWidth - margin - 50;

  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  doc.text("Administrative HoD", signatureX, finalY, { align: "left" });
  doc.text("(Signature and Seal)", signatureX, finalY + 6, { align: "left" });

  // Save PDF
  const sanitizedDeptName = departmentName.replace(/[^a-zA-Z0-9]/g, "_");
  const fileName = `Project_Status_${sanitizedDeptName}_${Date.now()}.pdf`;
  doc.save(fileName);
};

/**
 * Export Session Budget Department Wise to PDF
 */
export const exportSessionBudgetDepartmentWisePDF = (data, filters) => {
  const doc = new jsPDF(PDF_CONFIG);

  // Add header
  const startY = addPDFHeader(
    doc,
    "Session Budget - Department Wise",
    "Department-wise budget allocation summary for session projects",
    filters.financialYear,
  );

  // Prepare table data
  const tableData = [];
  let serialNumber = 1;

  data.departments.forEach((dept) => {
    tableData.push([
      serialNumber++,
      dept.departmentName,
      formatCurrency(dept.approvedTotal),
      formatCurrency(dept.approvedStatePlan),
      formatCurrency(dept.approvedNegotiatedLoan),
    ]);
  });

  // Calculate grand total
  const grandTotal = data.departments.reduce(
    (acc, dept) => ({
      approvedTotal: acc.approvedTotal + (dept.approvedTotal || 0),
      approvedStatePlan: acc.approvedStatePlan + (dept.approvedStatePlan || 0),
      approvedNegotiatedLoan:
        acc.approvedNegotiatedLoan + (dept.approvedNegotiatedLoan || 0),
    }),
    { approvedTotal: 0, approvedStatePlan: 0, approvedNegotiatedLoan: 0 },
  );

  // Create table
  doc.autoTable({
    startY: startY,
    head: [
      [
        "S.No.",
        "Department",
        "Proposed Total\nRequirement\n(in lakh)",
        "State Plan\n(in lakh)",
        "Negotiated Loan\n(in lakh)",
      ],
    ],
    body: tableData,
    foot: [
      [
        "",
        "Grand Total",
        formatCurrency(grandTotal.approvedTotal),
        formatCurrency(grandTotal.approvedStatePlan),
        formatCurrency(grandTotal.approvedNegotiatedLoan),
      ],
    ],
    theme: "grid",
    headStyles: {
      fillColor: [71, 85, 105],
      textColor: 255,
      fontSize: 9,
      fontStyle: "bold",
      halign: "center",
      valign: "middle",
    },
    bodyStyles: {
      fontSize: 8,
      valign: "middle",
    },
    footStyles: {
      fillColor: [31, 41, 55],
      textColor: 255,
      fontSize: 9,
      fontStyle: "bold",
      halign: "right",
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 15 },
      1: { halign: "left", cellWidth: 100 },
      2: { halign: "right", cellWidth: 40 },
      3: { halign: "right", cellWidth: 40 },
      4: { halign: "right", cellWidth: 40 },
    },
    margin: { top: startY, left: 10, right: 10 },
    didDrawPage: function (data) {
      // Add page number on each page
      const pageCount = doc.internal.getNumberOfPages();
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFontSize(8);
      doc.text(
        `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" },
      );
    },
  });

  // Save PDF
  const fileName = `Session_Budget_Department_Wise_${
    filters.financialYear
  }_${Date.now()}.pdf`;
  doc.save(fileName);
};

/**
 * Export State Plan Clearance Department Wise to PDF
 */
export const exportStatePlanClearanceDepartmentWisePDF = (data, filters) => {
  const doc = new jsPDF(PDF_CONFIG);

  const startY = addPDFHeader(
    doc,
    "State Plan Clearance - Department Wise",
    "Department-wise allocation summary showing proposed amounts vs allocated amounts from clearance",
    filters.financialYear,
  );

  const tableData = [];
  let serialNumber = 1;

  data.departments.forEach((dept) => {
    tableData.push([
      serialNumber++,
      dept.departmentName,
      formatCurrency(dept.approvedTotal),
      formatCurrency(dept.clearanceTotal),
      formatCurrency(dept.approvedStatePlan),
      formatCurrency(dept.clearanceStatePlan),
      formatCurrency(dept.approvedNegotiatedLoan),
      formatCurrency(dept.clearanceNegotiatedLoan),
    ]);
  });

  // Calculate grand totals
  const grandTotal = data.departments.reduce(
    (acc, dept) => ({
      approvedTotal: acc.approvedTotal + (dept.approvedTotal || 0),
      clearanceTotal: acc.clearanceTotal + (dept.clearanceTotal || 0),
      approvedStatePlan: acc.approvedStatePlan + (dept.approvedStatePlan || 0),
      clearanceStatePlan:
        acc.clearanceStatePlan + (dept.clearanceStatePlan || 0),
      approvedNegotiatedLoan:
        acc.approvedNegotiatedLoan + (dept.approvedNegotiatedLoan || 0),
      clearanceNegotiatedLoan:
        acc.clearanceNegotiatedLoan + (dept.clearanceNegotiatedLoan || 0),
    }),
    {
      approvedTotal: 0,
      clearanceTotal: 0,
      approvedStatePlan: 0,
      clearanceStatePlan: 0,
      approvedNegotiatedLoan: 0,
      clearanceNegotiatedLoan: 0,
    },
  );

  doc.autoTable({
    startY: startY,
    head: [
      [
        "S.No.",
        "Department",
        "Total\nApproved\nAmount\n(in lakh)",
        "Total\nCleared\nAmount\n(in lakh)",
        "State Plan\nApproved\n(in lakh)",
        "State Plan\nCleared\n(in lakh)",
        "Negotiated\nLoan\nApproved\n(in lakh)",
        "Negotiated\nLoan\nCleared\n(in lakh)",
      ],
    ],
    body: tableData,
    foot: [
      [
        "",
        "Grand Total",
        formatCurrency(grandTotal.approvedTotal),
        formatCurrency(grandTotal.clearanceTotal),
        formatCurrency(grandTotal.approvedStatePlan),
        formatCurrency(grandTotal.clearanceStatePlan),
        formatCurrency(grandTotal.approvedNegotiatedLoan),
        formatCurrency(grandTotal.clearanceNegotiatedLoan),
      ],
    ],
    theme: "grid",
    headStyles: {
      fillColor: [71, 85, 105],
      textColor: 255,
      fontSize: 8,
      fontStyle: "bold",
      halign: "center",
      valign: "middle",
    },
    bodyStyles: {
      fontSize: 7,
      valign: "middle",
    },
    footStyles: {
      fillColor: [31, 41, 55],
      textColor: 255,
      fontSize: 8,
      fontStyle: "bold",
      halign: "right",
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 12 },
      1: { halign: "left", cellWidth: 60 },
      2: { halign: "right", cellWidth: 28 },
      3: { halign: "right", cellWidth: 28 },
      4: { halign: "right", cellWidth: 28 },
      5: { halign: "right", cellWidth: 28 },
      6: { halign: "right", cellWidth: 28 },
      7: { halign: "right", cellWidth: 28 },
    },
    didDrawPage: function (data) {
      const pageCount = doc.internal.getNumberOfPages();
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFontSize(8);
      doc.text(
        `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" },
      );
    },
  });

  const fileName = `State_Plan_Clearance_Department_Wise_${
    filters.financialYear
  }_${Date.now()}.pdf`;
  doc.save(fileName);
};

/**
 * Export State Share Allocation Department Wise to PDF
 */
export const exportStateShareAllocationDepartmentWisePDF = (data, filters) => {
  const doc = new jsPDF(PDF_CONFIG);

  const startY = addPDFHeader(
    doc,
    "State Share Allocation - Department Wise",
    "Department-wise allocation summary and balance tracking",
    filters.financialYear,
  );

  const tableData = [];
  let serialNumber = 1;

  data.departments.forEach((dept) => {
    tableData.push([
      serialNumber++,
      dept.departmentName,
      formatCurrency(dept.clearedAgainstAllocation),
      formatCurrency(dept.clearedFromCommonPool),
      formatCurrency(dept.additionalStateShare),
      formatCurrency(dept.totalAmount),
      formatCurrency(dept.approvedAllocation),
      formatCurrency(dept.balanceAgainstAllocation),
    ]);
  });

  // Calculate grand totals
  const grandTotal = data.departments.reduce(
    (acc, dept) => ({
      clearedAgainstAllocation:
        acc.clearedAgainstAllocation + (dept.clearedAgainstAllocation || 0),
      clearedFromCommonPool:
        acc.clearedFromCommonPool + (dept.clearedFromCommonPool || 0),
      additionalStateShare:
        acc.additionalStateShare + (dept.additionalStateShare || 0),
      totalAmount: acc.totalAmount + (dept.totalAmount || 0),
      approvedAllocation:
        acc.approvedAllocation + (dept.approvedAllocation || 0),
      balanceAgainstAllocation:
        acc.balanceAgainstAllocation + (dept.balanceAgainstAllocation || 0),
    }),
    {
      clearedAgainstAllocation: 0,
      clearedFromCommonPool: 0,
      additionalStateShare: 0,
      totalAmount: 0,
      approvedAllocation: 0,
      balanceAgainstAllocation: 0,
    },
  );

  doc.autoTable({
    startY: startY,
    head: [
      [
        "S.No.",
        "Department",
        "Cleared\nAgainst\nAllocation\n(in lakh)",
        "Cleared\nFrom\nCommon\nPool\n(in lakh)",
        "Additional\nState\nShare\n(in lakh)",
        "Total\nAmount\n(in lakh)",
        "Approved\nAllocation\n(in lakh)",
        "Balance\nAgainst\nAllocation\n(in lakh)",
      ],
    ],
    body: tableData,
    foot: [
      [
        "",
        "Grand Total",
        formatCurrency(grandTotal.clearedAgainstAllocation),
        formatCurrency(grandTotal.clearedFromCommonPool),
        formatCurrency(grandTotal.additionalStateShare),
        formatCurrency(grandTotal.totalAmount),
        formatCurrency(grandTotal.approvedAllocation),
        formatCurrency(grandTotal.balanceAgainstAllocation),
      ],
    ],
    theme: "grid",
    headStyles: {
      fillColor: [71, 85, 105],
      textColor: 255,
      fontSize: 7.5,
      fontStyle: "bold",
      halign: "center",
      valign: "middle",
    },
    bodyStyles: {
      fontSize: 7,
      valign: "middle",
    },
    footStyles: {
      fillColor: [31, 41, 55],
      textColor: 255,
      fontSize: 8,
      fontStyle: "bold",
      halign: "right",
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 12 },
      1: { halign: "left", cellWidth: 55 },
      2: { halign: "right", cellWidth: 27 },
      3: { halign: "right", cellWidth: 27 },
      4: { halign: "right", cellWidth: 27 },
      5: { halign: "right", cellWidth: 27 },
      6: { halign: "right", cellWidth: 27 },
      7: { halign: "right", cellWidth: 27 },
    },
    didDrawPage: function (data) {
      const pageCount = doc.internal.getNumberOfPages();
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFontSize(8);
      doc.text(
        `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" },
      );
    },
  });

  const fileName = `State_Share_Allocation_Department_Wise_${
    filters.financialYear
  }_${Date.now()}.pdf`;
  doc.save(fileName);
};

/**
 * Export Central Clearance Overall Projects to PDF
 */
export const exportCentralClearanceOverallPDF = (data, filters) => {
  const doc = new jsPDF(PDF_CONFIG);

  const startY = addPDFHeader(
    doc,
    "Central Clearance - Overall Projects Status",
    "Comprehensive overview of all central projects with installments and UC status",
    filters.financialYear,
  );

  const tableData = [];
  let serialNumber = 1;

  data.forEach((project) => {
    // Truncate long project names
    const projectName =
      project.projectName.length > 40
        ? project.projectName.substring(0, 37) + "..."
        : project.projectName;
    const scheme =
      project.scheme.length > 30
        ? project.scheme.substring(0, 27) + "..."
        : project.scheme;

    tableData.push([
      serialNumber++,
      `${projectName}\n${scheme}`,
      formatCurrency(project.centralShare),
      formatCurrency(project.stateShare),
      formatCurrency(project.totalCost),
      formatCurrency(project.totalCentralReleased),
      formatCurrency(project.balanceToBeReleased),
      formatCurrency(project.stateCleared),
      formatCurrency(project.stateDue),
      formatCurrency(project.ucFurnished),
      formatCurrency(project.ucDue),
      formatCurrency(project.unspentBalance),
      project.remarks && project.remarks.length > 30
        ? project.remarks.substring(0, 27) + "..."
        : project.remarks || "-",
    ]);
  });

  doc.autoTable({
    startY: startY,
    head: [
      [
        "S.No.",
        "Project/Scheme",
        "Central\n(in lakh)",
        "State\n(in lakh)",
        "Total\n(in lakh)",
        "Released\n(in lakh)",
        "Balance\n(in lakh)",
        "State\nCleared\n(in lakh)",
        "State\nDue\n(in lakh)",
        "UC\nFurnished\n(in lakh)",
        "UC\nDue\n(in lakh)",
        "Unspent\n(in lakh)",
        "Remarks",
      ],
    ],
    body: tableData,
    theme: "grid",
    headStyles: {
      fillColor: [71, 85, 105],
      textColor: 255,
      fontSize: 6.5,
      fontStyle: "bold",
      halign: "center",
      valign: "middle",
    },
    bodyStyles: {
      fontSize: 6,
      valign: "middle",
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 10 },
      1: { halign: "left", cellWidth: 45 },
      2: { halign: "right", cellWidth: 16 },
      3: { halign: "right", cellWidth: 16 },
      4: { halign: "right", cellWidth: 16 },
      5: { halign: "right", cellWidth: 16 },
      6: { halign: "right", cellWidth: 16 },
      7: { halign: "right", cellWidth: 16 },
      8: { halign: "right", cellWidth: 16 },
      9: { halign: "right", cellWidth: 16 },
      10: { halign: "right", cellWidth: 16 },
      11: { halign: "right", cellWidth: 16 },
      12: { halign: "left", cellWidth: 30 },
    },
    didDrawPage: function (data) {
      const pageCount = doc.internal.getNumberOfPages();
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFontSize(8);
      doc.text(
        `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" },
      );
    },
  });

  const fileName = `Central_Clearance_Overall_${
    filters.financialYear
  }_${Date.now()}.pdf`;
  doc.save(fileName);
};

/**
 * Export Ongoing Projects to PDF
 */
export const exportOngoingProjectsPDF = (projects, departmentName, filters) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;

  // Header - Government of Nagaland
  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.text("Government of Nagaland", pageWidth / 2, 15, { align: "center" });

  // Department name (if provided)
  if (departmentName) {
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text(`Department of - ${departmentName}`, pageWidth / 2, 23, {
      align: "center",
    });
  }

  // Title
  doc.setFontSize(12);
  doc.setFont(undefined, "bold");
  doc.text("Ongoing Projects Report", pageWidth / 2, departmentName ? 31 : 23, {
    align: "center",
  });

  // Financial Year and Export Date
  const headerY = departmentName ? 39 : 31;
  doc.setFontSize(9);
  doc.setFont(undefined, "normal");
  doc.text(
    `Financial Year: ${filters.financialYear || "All Years"}`,
    margin,
    headerY,
  );

  const exportDate = new Date().toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  doc.text(`Exported: ${exportDate}`, pageWidth - margin, headerY, {
    align: "right",
  });

  // Group projects by department
  const groupedProjects = {};
  projects.forEach((project) => {
    const dept = project.department;
    if (!groupedProjects[dept]) {
      groupedProjects[dept] = [];
    }
    groupedProjects[dept].push(project);
  });

  const departmentKeys = Object.keys(groupedProjects).sort();

  // Prepare table data
  const tableData = [];
  let serialNumber = 1;

  departmentKeys.forEach((dept) => {
    const deptProjects = groupedProjects[dept];

    deptProjects.forEach((project) => {
      tableData.push([
        serialNumber++,
        dept,
        project.projectName,
        project.yearOfCommencement || "N/A",
        formatCurrency(project.totalEstimatedCost),
        formatCurrency(project.totalExpenditure),
        formatCurrency(project.balanceRemaining),
      ]);
    });

    // Add department total row
    const deptTotals = {
      totalEstimatedCost: deptProjects.reduce(
        (sum, p) => sum + (parseFloat(p.totalEstimatedCost) || 0),
        0,
      ),
      totalExpenditure: deptProjects.reduce(
        (sum, p) => sum + (parseFloat(p.totalExpenditure) || 0),
        0,
      ),
      balanceRemaining: deptProjects.reduce(
        (sum, p) => sum + (parseFloat(p.balanceRemaining) || 0),
        0,
      ),
    };

    tableData.push([
      {
        content: `Total - ${dept} (${deptProjects.length} projects)`,
        colSpan: 4,
        styles: { fontStyle: "bold", fillColor: [107, 114, 128] },
      },
      {
        content: formatCurrency(deptTotals.totalEstimatedCost),
        styles: {
          fontStyle: "bold",
          fillColor: [107, 114, 128],
          textColor: 255,
        },
      },
      {
        content: formatCurrency(deptTotals.totalExpenditure),
        styles: {
          fontStyle: "bold",
          fillColor: [107, 114, 128],
          textColor: 255,
        },
      },
      {
        content: formatCurrency(deptTotals.balanceRemaining),
        styles: {
          fontStyle: "bold",
          fillColor: [107, 114, 128],
          textColor: 255,
        },
      },
    ]);
  });

  // Calculate grand totals
  const grandTotals = {
    totalEstimatedCost: projects.reduce(
      (sum, p) => sum + (parseFloat(p.totalEstimatedCost) || 0),
      0,
    ),
    totalExpenditure: projects.reduce(
      (sum, p) => sum + (parseFloat(p.totalExpenditure) || 0),
      0,
    ),
    balanceRemaining: projects.reduce(
      (sum, p) => sum + (parseFloat(p.balanceRemaining) || 0),
      0,
    ),
  };

  // Create table
  const startY = departmentName ? 45 : 37;

  doc.autoTable({
    startY: startY,
    head: [
      [
        "S.No.",
        "Department",
        "Project Name",
        "Year of\nCommencement",
        "Total Approved Cost\n(in lakh)",
        "Total Clearance\n(in lakh)\n(including CFY)",
        "Balance Remaining\n(in lakh)",
      ],
    ],
    body: tableData,
    foot: [
      [
        {
          content: `Grand Total (${projects.length} projects)`,
          colSpan: 4,
          styles: { fontStyle: "bold" },
        },
        formatCurrency(grandTotals.totalEstimatedCost),
        formatCurrency(grandTotals.totalExpenditure),
        formatCurrency(grandTotals.balanceRemaining),
      ],
    ],
    theme: "grid",
    headStyles: {
      fillColor: [71, 85, 105],
      textColor: 255,
      fontSize: 8,
      fontStyle: "bold",
      halign: "center",
      valign: "middle",
    },
    bodyStyles: {
      fontSize: 7,
      valign: "middle",
    },
    footStyles: {
      fillColor: [31, 41, 55],
      textColor: 255,
      fontSize: 8,
      fontStyle: "bold",
      halign: "right",
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 15 },
      1: { halign: "center", cellWidth: 40 },
      2: { halign: "left", cellWidth: 80 },
      3: { halign: "center", cellWidth: 25 },
      4: { halign: "right", cellWidth: 35 },
      5: { halign: "right", cellWidth: 35 },
      6: { halign: "right", cellWidth: 35 },
    },
    margin: { left: margin, right: margin },
    didDrawPage: function (data) {
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.text(
        `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" },
      );
    },
  });

  // Save PDF
  const sanitizedDeptName = departmentName
    ? departmentName.replace(/[^a-zA-Z0-9]/g, "_")
    : "All_Departments";
  const fileName = `Ongoing_Projects_${sanitizedDeptName}_${Date.now()}.pdf`;
  doc.save(fileName);
};
